import React, { createContext, useContext, ReactNode, useState } from 'react';
import { useRouter } from 'next/router';
import { isBounceWebview, postMessageToRN } from 'legacy/common/utils/webview';
import { WEBVIEW_CLOSE } from 'legacy/common/constants/webview';
import { Event } from 'legacy/common/types/models';
import { OffersStep } from '../offers.types';
import { EventForUser, EventAnalyticsTicketScansByTierDto } from '../types';
import { useGetEventAnalyticsTicketScanByTier } from '../hooks/useGetEventAnalyticsTicketScanByTier';

export enum Screens {
  BASIC_FORM = 'basicForm',
  REQUEST_TO_PUBLISH_FORM = 'requestToPublishForm',
  EVENT_SETTINGS = 'eventSettings',
  TRACKING_LINKS = 'trackingLinks',
  DISCOUNT_CODES = 'discountCodes',
  RECURRING_SETTINGS = 'recurringSettings',
  PRICING_TIERS = 'pricingTiers',
  OFFERS = 'offers',
  PRE_PURCHASE_QUESTIONS = 'prePurchaseQuestions'
}

interface EventEditorContextProps {
  // Basic event info
  eventId: string;
  event: EventForUser;
  associationId?: string;
  isDraft: boolean;
  isEditing: boolean;
  isLoading: boolean;
  lastUpdated?: string;
  eventSeriesId?: string;
  eventShareToken?: string;
  shouldRequestToPublish: boolean;
  requestToPublishFormId?: string;
  requestToPublishWorkflowId?: string;
  startDate?: string;

  // Offers state
  offersStep: OffersStep;
  setOffersStep: (step: OffersStep) => void;

  // Tier scans data
  tierScansData?: EventAnalyticsTicketScansByTierDto;
  isTierScansLoading: boolean;
  refetchTierScans: () => void;

  // Section tier management
  addTierToSectionId: string | undefined;
  setAddTierToSectionId: (sectionId: string | undefined) => void;

  // Navigation functions
  handleBack: () => void;
  navigateToScreen: (screen: Screens) => void;
  navigateToBasicForm: () => void;
  navigateToEventSettings: () => void;
  navigateToRecurringSettings: () => void;
  navigateToTrackingLinks: () => void;
  navigateToDiscountCodes: () => void;
  navigateToOffersPage: () => void;
  navigateToSeriesSettings: () => void;
  togglePricingTiersModal: () => void;
  handleOfferBackNavigation: () => void;
  navigateToPrePurchaseQuestions: () => void;
  // Event actions
  createDraft: (values?: any) => Promise<Event>;
  requestPublication: (data?: { [key: string]: unknown }) => Promise<void>;
  requestPublicationWorkflow: (data?: {
    [key: string]: unknown;
  }) => Promise<void>;
  refetchEvent: () => void;

  // Current screen
  currentScreen: Screens;
}

const EventEditorContext = createContext<EventEditorContextProps | undefined>(
  undefined
);

export interface EventEditorProviderProps {
  children: ReactNode;
  associationId?: string;
  eventId: string;
  event: EventForUser;
  isDraft: boolean;
  isEditing: boolean;
  isLoading: boolean;
  lastUpdated?: string;
  eventSeriesId?: string;
  eventShareToken?: string;
  shouldRequestToPublish: boolean;
  requestToPublishFormId?: string;
  requestToPublishWorkflowId?: string;
  startDate?: string;
  offersStep: OffersStep;
  setOffersStep: (step: OffersStep) => void;
  createDraft: (values?: any) => Promise<Event>;
  requestPublication: (data?: { [key: string]: unknown }) => Promise<void>;
  requestPublicationWorkflow: (data?: {
    [key: string]: unknown;
  }) => Promise<void>;
  refetchEvent: () => void;
  currentScreen: Screens;
  setCurrentScreen: (screen: Screens) => void;
}

export const EventEditorProvider: React.FC<EventEditorProviderProps> = ({
  children,
  associationId,
  eventId,
  event,
  isDraft,
  isEditing,
  isLoading,
  lastUpdated,
  eventSeriesId,
  eventShareToken,
  shouldRequestToPublish,
  requestToPublishFormId,
  requestToPublishWorkflowId,
  startDate,
  offersStep,
  setOffersStep,
  createDraft,
  requestPublication,
  requestPublicationWorkflow,
  refetchEvent,
  currentScreen,
  setCurrentScreen
}) => {
  const router = useRouter();

  const [addTierToSectionId, setAddTierToSectionId] = useState<
    string | undefined
  >(undefined);

  // Fetch tier-scans data
  const {
    data: tierScansData,
    isLoading: isTierScansLoading,
    refetch: refetchTierScans
  } = useGetEventAnalyticsTicketScanByTier({
    eventId
  });

  const navigateToScreen = (screen: Screens) => {
    setCurrentScreen(screen);
  };

  const navigateToBasicForm = () => navigateToScreen(Screens.BASIC_FORM);
  const navigateToEventSettings = () =>
    navigateToScreen(Screens.EVENT_SETTINGS);
  const navigateToRecurringSettings = () =>
    navigateToScreen(Screens.RECURRING_SETTINGS);
  const navigateToTrackingLinks = () =>
    navigateToScreen(Screens.TRACKING_LINKS);
  const navigateToDiscountCodes = () =>
    navigateToScreen(Screens.DISCOUNT_CODES);
  const navigateToOffersPage = () => navigateToScreen(Screens.OFFERS);
  const navigateToSeriesSettings = () =>
    navigateToScreen(Screens.RECURRING_SETTINGS);
  const togglePricingTiersModal = () => navigateToScreen(Screens.PRICING_TIERS);
  const navigateToPrePurchaseQuestions = () =>
    navigateToScreen(Screens.PRE_PURCHASE_QUESTIONS);
  const handleOfferBackNavigation = () => {
    switch (offersStep) {
      case OffersStep.EDIT_OFFER:
        setOffersStep(OffersStep.OFFER_DETAILS);
        break;
      case OffersStep.OFFER_DETAILS:
        setOffersStep(OffersStep.OFFERS_LIST);
        break;
      default:
        navigateToScreen(Screens.EVENT_SETTINGS);
        break;
    }
  };

  const handleBack = () => {
    switch (currentScreen) {
      case Screens.EVENT_SETTINGS:
      case Screens.RECURRING_SETTINGS:
      case Screens.PRICING_TIERS:
      case Screens.REQUEST_TO_PUBLISH_FORM:
        navigateToScreen(Screens.BASIC_FORM);
        break;

      case Screens.TRACKING_LINKS:
      case Screens.DISCOUNT_CODES:
        navigateToScreen(Screens.EVENT_SETTINGS);
        break;

      case Screens.OFFERS:
        handleOfferBackNavigation();
        break;

      case Screens.PRE_PURCHASE_QUESTIONS:
        navigateToScreen(Screens.EVENT_SETTINGS);
        break;

      default:
        router.back();
        if (isBounceWebview()) {
          postMessageToRN(WEBVIEW_CLOSE);
        }
    }
  };

  const value = {
    associationId,
    eventId,
    event,
    isDraft,
    isEditing,
    isLoading,
    lastUpdated,
    eventSeriesId,
    eventShareToken,
    shouldRequestToPublish,
    requestToPublishFormId,
    requestToPublishWorkflowId,
    startDate,
    offersStep,
    setOffersStep,
    tierScansData,
    isTierScansLoading,
    refetchTierScans,
    handleBack,
    navigateToScreen,
    navigateToBasicForm,
    navigateToEventSettings,
    navigateToRecurringSettings,
    navigateToTrackingLinks,
    navigateToDiscountCodes,
    navigateToOffersPage,
    navigateToSeriesSettings,
    togglePricingTiersModal,
    handleOfferBackNavigation,
    navigateToPrePurchaseQuestions,
    createDraft,
    requestPublication,
    requestPublicationWorkflow,
    refetchEvent,
    currentScreen,
    addTierToSectionId,
    setAddTierToSectionId
  };

  return (
    <EventEditorContext.Provider value={value}>
      {children}
    </EventEditorContext.Provider>
  );
};

export const useEventEditorContext = () => {
  const context = useContext(EventEditorContext);
  if (context === undefined) {
    throw new Error(
      'useEventEditorContext must be used within an EventEditorProvider'
    );
  }
  return context;
};
